import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, CurrencyJpy, Warning, Clock, ArrowDown, ArrowUp, ArrowRight, Stack, Rocket, Plus, Wrench } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { mfgApi, MOVE_BADGE } from './mfgApi';
import { formatShort } from '@/utils/datetime';

const yen = (n) => `¥${Math.round(n || 0).toLocaleString()}`;
const fmtTime = (iso) => { try { return formatShort(iso); } catch { return ''; } };

function SectionCard({ title, Icon, to, viewLabel, children, testid }) {
  return (
    <section className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-900">{Icon && <Icon size={15} weight="duotone" className="text-zinc-400" />}{title}</h2>
        {to && <Link to={to} className="flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800">{viewLabel} <ArrowRight size={12} weight="bold" /></Link>}
      </div>
      {children}
    </section>
  );
}

function QuickStep({ n, title, desc }) {
  return (
    <li className="flex items-start gap-2.5 rounded-lg bg-zinc-50 border border-zinc-200 p-3">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-700 text-white text-xs font-bold tabular-nums shrink-0">{n}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-zinc-800">{title}</p>
        <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </li>
  );
}

export default function MfgDashboard() {
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { mfgApi.dashboard().then(setData).catch(() => {}); }, []);

  const stats = data?.stats || {};
  const low = data?.low_stock_items || [];
  const expiring = data?.expiring_lots || [];
  const moves = data?.recent_moves || [];
  const isEmpty = data && !(stats.total_items > 0);
  const nothingToDo = !!data && !stats.low_stock && !stats.expiring_lots && !stats.overstock;

  return (
    <div className="space-y-8 page-enter" data-testid="mfg-dashboard">
      <PageHeader testid="mfg-dashboard-header" title={t('mfg.dash.title')} description={t('mfg.dash.subtitle')} />

      {/* Onboarding guide — only while there is no inventory yet */}
      {isEmpty && (
        <section className="bg-white rounded-lg border border-zinc-200 p-4 sm:p-5" data-testid="mfg-dash-quickstart">
          <div className="flex items-start gap-3">
            <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-teal-700 text-white shrink-0"><Rocket size={18} weight="duotone" /></div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-zinc-900">{t('mfg.dash.quickStart.title')}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{t('mfg.dash.quickStart.subtitle')}</p>
              <ol className="mt-3 grid sm:grid-cols-3 gap-2.5">
                <QuickStep n={1} title={t('mfg.dash.quickStart.s1Title')} desc={t('mfg.dash.quickStart.s1Desc')} />
                <QuickStep n={2} title={t('mfg.dash.quickStart.s2Title')} desc={t('mfg.dash.quickStart.s2Desc')} />
                <QuickStep n={3} title={t('mfg.dash.quickStart.s3Title')} desc={t('mfg.dash.quickStart.s3Desc')} />
              </ol>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link to="/mfg/inventory?new=1" data-testid="mfg-dash-quickstart-cta" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-colors">
                  <Plus size={15} weight="bold" /> {t('mfg.dash.quickStart.cta')}
                </Link>
                <Link to="/mfg/inventory" data-testid="mfg-dash-quickstart-inventory" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-zinc-200 bg-white text-zinc-700 text-sm font-medium hover:bg-zinc-50 transition-colors">
                  {t('mfg.dash.quickStart.openInventory')} <ArrowRight size={13} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Zone 1 — 対応が必要 */}
      <DashboardZone title="対応が必要" hint="在庫の例外だけを表示します" testid="mfg-action-zone">
        {nothingToDo ? (
          <AllClear message="在庫の例外はありません" testid="mfg-allclear" />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <ActionTile icon={Warning} label={t('mfg.dash.stat.low')} hint="発注点を下回る品目" value={stats.low_stock ?? 0} urgent
              onClick={() => navigate('/mfg/inventory')} testid="mfg-dash-stat-low" />
            <ActionTile icon={Clock} label={t('mfg.dash.stat.expiring')} hint="期限が近いロット" value={stats.expiring_lots ?? 0} urgent
              onClick={() => navigate('/mfg/inventory')} testid="mfg-dash-stat-expiring" />
            <ActionTile icon={Stack} label={t('mfg.dash.stat.overstock')} hint="上限を超える在庫" value={stats.overstock ?? 0}
              onClick={() => navigate('/mfg/inventory')} testid="mfg-dash-stat-overstock" />
          </div>
        )}
      </DashboardZone>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Low stock */}
        <SectionCard title={t('mfg.dash.lowStock')} Icon={Warning} to="/mfg/inventory" viewLabel={t('mfg.dash.viewAll')} testid="mfg-dash-low-list">
          {low.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-zinc-400">{t('mfg.dash.noLow')}</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {low.map((i) => (
                <li key={i.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`mfg-dash-low-${i.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{i.name}</p>
                    <p className="text-[11px] text-zinc-400">{i.sku} · {t('mfg.dash.reorderAt')} {i.reorder_point}</p>
                  </div>
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 tabular-nums">{i.on_hand} {i.uom || ''}</Badge>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Expiring */}
        <SectionCard title={t('mfg.dash.expiring')} Icon={Clock} testid="mfg-dash-expiring-list">
          {expiring.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-zinc-400">{t('mfg.dash.noExpiring')}</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {expiring.map((e) => (
                <li key={e.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`mfg-dash-exp-${e.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{e.item_name || e.item_id}</p>
                    <p className="text-[11px] text-zinc-400 font-mono">{e.lot_no} · {e.quantity} {e.uom || ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-600 tabular-nums">{e.expiry_date}</p>
                    {e.expired
                      ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t('mfg.dash.expired')}</Badge>
                      : <span className={`text-[11px] font-medium tabular-nums ${e.days_left <= 7 ? 'text-rose-600' : 'text-amber-600'}`}>{t('mfg.dash.daysLeft')}{e.days_left != null ? ` -${e.days_left}` : ''}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Recent moves */}
      <SectionCard title={t('mfg.dash.recentMoves')} Icon={Clock} to="/mfg/moves" viewLabel={t('mfg.dash.viewAll')} testid="mfg-dash-recent-moves">
        {moves.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-zinc-400">{t('mfg.dash.noMoves')}</p>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {moves.map((m) => {
              const up = (m.qty_delta || 0) >= 0;
              return (
                <li key={m.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`mfg-dash-move-${m.id}`}>
                  <Badge variant="outline" className={MOVE_BADGE[m.type] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}>{m.type_label}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{m.item_name || '—'}{m.lot_no && <span className="ml-1.5 font-mono text-[11px] text-zinc-400">{m.lot_no}</span>}</p>
                    <p className="text-[11px] text-zinc-400 truncate">{m.reason || ''}{m.by_name ? ` · ${m.by_name}` : ''}</p>
                  </div>
                  <span className={`flex items-center gap-0.5 text-sm font-semibold tabular-nums ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {up ? <ArrowUp size={13} weight="bold" /> : <ArrowDown size={13} weight="bold" />}{Math.abs(m.qty_delta || 0)} {m.uom || ''}
                  </span>
                  <span className="text-[11px] text-zinc-400 tabular-nums w-20 text-right shrink-0">{fmtTime(m.at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      <MetricsDisclosure title="参考指標" testid="mfg-metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          {[
            { label: t('mfg.dash.stat.items'), value: stats.total_items ?? 0, Icon: Package },
            { label: t('mfg.dash.stat.value'), value: yen(stats.total_value), Icon: CurrencyJpy },
            { label: t('mfg.dash.stat.subcontract'), value: `${stats.subcontract_dependency ?? 0}%`, Icon: Wrench },
            { label: t('mfg.dash.valueByCategory'), value: `${(data?.value_by_category || []).filter((c) => c.value > 0).length} 分類`, Icon: Stack },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-zinc-200 p-3">
              <p className="flex items-center gap-1.5 text-[11px] text-zinc-500"><k.Icon size={13} weight="duotone" className="text-zinc-400" />{k.label}</p>
              <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{k.value}</p>
            </div>
          ))}
        </div>
      </MetricsDisclosure>
    </div>
  );
}
